/**
 * The SVG palette sheet.
 *
 * SVG is the one export a person actually *looks at* — it opens in a browser, in
 * Figma, in Illustrator — so it is drawn to look like the palette on the site
 * rather than like a data dump: the same near-black canvas, the same swatch card
 * (4px gray-900 frame, rounded-lg corners, the belly divider, the white info
 * panel with name / value / pill), the same grid, the same type. Everything the
 * site renders with Tailwind classes is re-derived here as geometry, because an
 * SVG file has no stylesheet to lean on.
 *
 * Kept separate from utils/paletteFile.ts, and dependency-free in both
 * directions: paletteFile hands it a flat list of groups and gets a string back,
 * so this module never has to know how a palette is shaped or parsed, and stays
 * isomorphic (browser download + `POST /api/saveFile`) with no runtime imports.
 *
 * Reference implementations, if any of this needs to be kept in sync:
 *   - components/molecules/ColorSwatchCard.vue — the card
 *   - components/molecules/ColorGroup.vue      — the titled grid
 *   - tailwind.config.js / assets/css/main.css — the type stack
 */
import type { PaletteColor } from './paletteFile'

/** A titled run of swatches: one `<ColorGroup>` on the site. */
export interface PaletteGroup {
  name: string
  colors: PaletteColor[]
}

/**
 * The site's palette, as hex. These are the Tailwind tokens the components
 * actually use — bg-gray-950 for the page, border-gray-900 for the card frame,
 * text-gray-500/600 for the two info lines, bg-gray-200 for the pill.
 */
const THEME = {
  canvas: '#030712',
  hairline: '#1f2937',
  cardEdge: '#111827',
  panel: '#ffffff',
  heading: '#ffffff',
  muted: '#6b7280',
  value: '#4b5563',
  pill: '#e5e7eb',
  pillText: '#111827',
  checker: '#e7e9ee',
  accent: '#6366f1',
}

/**
 * The site's type is "Inter Variable", loaded as a web font — which a standalone
 * SVG cannot rely on. Embedding it would mean ~50 KB of base64 woff2 in every
 * download (and Figma/Illustrator ignore @font-face regardless), so the sheet
 * asks for locally installed Inter first and then falls back through the system
 * UI faces, which are the closest metric matches. Set once on the root <svg>:
 * text inherits it, and inherited presentation attributes are the one styling
 * mechanism every SVG renderer agrees on.
 */
const FONT_FAMILY =
  "Inter, 'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

/** Sheet-level layout. `columns` mirrors the site's `sm:grid-cols-3` + `gap-4`. */
const SHEET = {
  pad: 32,
  gap: 16,
  maxColumns: 4,
  /** Space between the end of one group's grid and the next group's title. */
  groupGap: 28,
  headingSize: 16,
  /** `mb-2` under the site's <h2>. */
  headingGap: 8,
}

/**
 * The swatch card, measured off the component's box model rather than eyeballed,
 * so a card here is the card the site draws at this width:
 *
 *   - `width` 192 leaves 184px inside the frame, and 184 is the one width near
 *     the site's own (~181px in its content column) where belly.svg's 69×9
 *     viewBox scales to a whole number of pixels — 24 — which keeps every
 *     coordinate under it an integer.
 *   - `colorBand` is the flat part of the color area: `py-7` (28 + 28) less the
 *     `-mt-1` overlap. It is fixed on the site too — only the belly scales with
 *     the card's width — and the color continues *behind* the wave.
 *   - `panel` is `pt-2` + two 16px lines + the pill's 8px offset + a 20px pill +
 *     `pb-4`, which is what makes the card ~166–168px tall in the app.
 */
const CARD = {
  width: 192,
  border: 4,
  radius: 8,
  colorBand: 52,
  panel: 84,
  /** `px-4`. The site goes to `px-6` at sm, but 16 leaves long values room. */
  padX: 16,
}

/** assets/img/belly.svg — its viewBox and its single path, inlined. */
const BELLY = {
  width: 69,
  height: 9,
  path:
    'M69 9L0 8.99999L2.86111e-07 2.72727C2.86111e-07 2.72727 9.46335 -6.8073e-07 17.25 0C25.0367 ' +
    '6.8073e-07 34.5 2.72727 34.5 2.72727C34.5 2.72727 43.9633 5.45454 51.75 5.45454C59.5367 ' +
    '5.45454 69 2.72727 69 2.72727V9Z',
}

/**
 * public/favicon.svg — the brand mark, for the sheet's header. Drawn 30px wide,
 * with `gutter` the offset the two header lines start at (mark + 12px).
 */
const MARK = {
  size: 28,
  gutter: 42,
  arc:
    'M21.6178 20.2076C21.1979 19.9651 20.6685 20.0635 20.3235 20.4042C18.6981 22.0092 16.4648 23 14 23C9.02944 ' +
    '23 5 18.9706 5 14C5 9.02944 9.02944 5 14 5C16.1741 5 18.1682 5.77089 19.7237 7.05425C20.0695 7.33947 ' +
    '20.5558 7.40563 20.9439 7.18153L23.6909 5.59557C24.2295 5.28457 24.3577 4.56047 23.9187 4.11979C21.3837 ' +
    '1.57496 17.8758 0 14 0C6.26801 0 0 6.26801 0 14C0 21.732 6.26801 28 14 28C18.2167 28 21.998 26.1358 ' +
    '24.5646 23.1866C24.9565 22.7364 24.8126 22.0521 24.2957 21.7536L21.6178 20.2076Z',
  blob:
    'M14.25 20.5C17.9779 20.5 21 17.5401 21 13.8889C21 13.5282 20.9705 13.1743 20.9138 12.8293C20.6961 ' +
    '12.9172 20.0897 13.1512 19.3624 13.3362L19.3125 13.3487C18.7765 13.4823 18.1791 13.5873 17.625 ' +
    '13.5873C16.1015 13.5873 14.25 12.7937 14.25 12.7937C14.25 12.7937 12.3985 12 10.875 12C9.56094 12 ' +
    '8.00281 12.5905 7.59929 12.7528C7.53402 13.1219 7.5 13.5015 7.5 13.8889C7.5 17.5401 10.5221 20.5 ' +
    '14.25 20.5Z',
}

// Derived card geometry, so the numbers below stay readable.
const INNER_W = CARD.width - CARD.border * 2
const BELLY_SCALE = INNER_W / BELLY.width
const BELLY_H = BELLY.height * BELLY_SCALE
/** Height of the color fill, including the part hidden behind the wave. */
const COLOR_H = CARD.colorBand + BELLY_H
const CARD_H = CARD.border * 2 + COLOR_H + CARD.panel
const INNER_H = CARD_H - CARD.border * 2
const PANEL_TOP = CARD.border + COLOR_H
const TEXT_W = INNER_W - CARD.padX * 2
// text-xs: two 12px lines on 16px leading, then the pill 8px (mt-2) below them.
// The baselines sit 12.4px under each line box, which is Inter's ascent plus the
// half-leading a 16px line box adds around a 12px face.
const NAME_BASELINE = PANEL_TOP + 8 + 12.4
const VALUE_BASELINE = NAME_BASELINE + 16
const PILL_TOP = PANEL_TOP + 8 + 32 + 8
const PILL_H = 20
const PILL_SIZE = 11.5
/** `px-2` on the site's pill. */
const PILL_PAD_X = 8
/** Cap-centred in the pill: half its height, plus half a cap. */
const PILL_BASELINE = PILL_TOP + PILL_H / 2 + PILL_SIZE * 0.357

/* -------------------------------------------------------------- utilities -- */

/**
 * Color names and values are interpolated straight into markup. They come from a
 * third-party site's CSS, so a `&` or a stray `<` would otherwise produce an SVG
 * that no parser will open (the VPS emitted these unescaped).
 */
function escapeXML(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Trim float noise out of the markup: 93.04347826086956 → 93.04. */
function n(value: number, precision = 2): string {
  const factor = 10 ** precision
  return String(Math.round(value * factor) / factor)
}

/**
 * Reference a shape from <defs>. Both href forms: SVG 2 renderers read `href`,
 * and the design tools that predate it only read the xlink one — a palette that
 * loses its belly divider in Illustrator is not worth the 20 saved bytes.
 */
function use(id: string): string {
  return `<use href="#${id}" xlink:href="#${id}"/>`
}

/**
 * Advance widths as a fraction of the font size, keyed by the characters that
 * share one. There is no layout engine here and SVG text neither wraps nor
 * clips, so these decide where a name gets an ellipsis and how wide the pill is
 * drawn around its label.
 *
 * Measured off Inter itself — @fontsource-variable/inter rendered in a browser,
 * 20 copies of each glyph, at weights 400/600/700 — and bucketed to 0.02em,
 * which is a quarter of a pixel at the sizes used here. Guessing instead is what
 * made the pill's right padding look short: `%` is more than three times the
 * width of `·` and 1.6× an `e`, and a hex value with an alpha percentage is
 * mostly made of the characters a flat "letters are ~0.55em" rule gets wrong.
 *
 * Each bucket's value is the one that centres the error between the regular face
 * and BOLD_FACTOR × the semibold one, so both weights come out slightly over
 * rather than under — a pill a pixel too roomy is invisible, a label that
 * outgrows its pill is not.
 */
const GLYPH_WIDTHS: ReadonlyArray<readonly [number, string]> = [
  [0.26, 'Iijl'], [0.3, '!,.:·'], [0.32, "';\\"], [0.34, '`ft|'], [0.36, '()/[]'],
  [0.4, 'r'], [0.42, '1'], [0.44, '{}'], [0.46, '-_'], [0.48, '^'],
  [0.5, '"'], [0.52, '*?'], [0.54, 's'], [0.56, 'JLackvxz'], [0.58, '7Fey'],
  [0.6, '25Ehnou'], [0.62, '3689bdgpq'], [0.64, '#$&04BPRSTZ'], [0.66, '+<=>~'], [0.68, 'K'],
  [0.7, 'XY'], [0.72, 'ACDUV'], [0.74, 'GHN'], [0.76, 'OQ'], [0.82, 'w'],
  [0.88, 'm'], [0.9, 'M…'], [0.98, '%@—'], [1.02, 'W'],
]

/** Space, which is the one glyph that gets *narrower* as the weight goes up. */
const SPACE_WIDTH = 0.26

/** Everything unlisted — accented letters, non-Latin scripts — takes a capital's
 * average, since over-measuring only costs an early ellipsis. */
const DEFAULT_WIDTH = 0.68

/** Semibold and bold run ~3% wider than the regular face. */
const BOLD_FACTOR = 1.03

/**
 * Inter kerns, so a set string is tighter than the sum of its glyphs' advances —
 * by 1.9% on average over every label, value and group name in the sample palette
 * (555 strings), which is where this comes from. Without it the estimate is
 * biased wide on exactly the strings that have the most kern pairs, `rgb(…)`
 * among them, and their pills come out visibly roomier than the rest.
 */
const KERNING_FACTOR = 0.98

const GLYPH_WIDTH = new Map<string, number>([[' ', SPACE_WIDTH]])
for (const [width, chars] of GLYPH_WIDTHS) {
  for (const char of chars) GLYPH_WIDTH.set(char, width)
}

function textWidth(text: string, size: number, bold = false): number {
  let width = 0
  for (const char of text) width += GLYPH_WIDTH.get(char) ?? DEFAULT_WIDTH
  return width * size * KERNING_FACTOR * (bold ? BOLD_FACTOR : 1)
}

/** Clip to `maxWidth` with an ellipsis, since SVG text neither wraps nor clips. */
function fitText(text: string, maxWidth: number, size: number, bold = false): string {
  if (textWidth(text, size, bold) <= maxWidth) return text
  const budget = maxWidth - textWidth('…', size, bold)
  let out = ''
  let width = 0
  for (const char of text) {
    const advance = textWidth(char, size, bold)
    if (width + advance > budget) break
    out += char
    width += advance
  }
  return out.replace(/[\s-]+$/, '') + '…'
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

/* ----------------------------------------------------------------- colors -- */

interface Rgba {
  r: number
  g: number
  b: number
  a: number
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/** `.05` / `5%` → 0…1. */
function unit(token: string): number {
  const value = parseFloat(token)
  if (!Number.isFinite(value)) return 1
  return clamp(token.includes('%') ? value / 100 : value, 0, 1)
}

/** `156` / `61%` → 0…255. */
function byte(token: string): number {
  const value = parseFloat(token)
  if (!Number.isFinite(value)) return 0
  return clamp(Math.round(token.includes('%') ? (value / 100) * 255 : value), 0, 255)
}

function hslToRgb(h: number, s: number, l: number): Rgba {
  const hue = ((h % 360) + 360) % 360
  const chroma = (1 - Math.abs(2 * l - 1)) * s
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = l - chroma / 2
  const [r, g, b] =
    hue < 60 ? [chroma, x, 0]
    : hue < 120 ? [x, chroma, 0]
    : hue < 180 ? [0, chroma, x]
    : hue < 240 ? [0, x, chroma]
    : hue < 300 ? [x, 0, chroma]
    : [chroma, 0, x]
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
    a: 1,
  }
}

/**
 * Parse the notations a scraped stylesheet actually yields: #rgb[a], #rrggbb[aa],
 * rgb()/rgba() and hsl()/hsla(), comma- or space-separated. Used for two things —
 * splitting alpha out of the fill (so the checkerboard shows through, and so the
 * `fill` attribute stays a plain hex that every renderer accepts, not an rgba()
 * string only browsers take) and printing the value in its other notation on the
 * pill. Anything unrecognised falls back to being passed through verbatim.
 */
function parseColor(raw: string): Rgba | null {
  const value = String(raw).trim().toLowerCase()
  if (!value) return null
  if (value === 'transparent') return { r: 0, g: 0, b: 0, a: 0 }

  const hex = /^#([0-9a-f]+)$/.exec(value)
  if (hex) {
    const digits = hex[1]
    if (digits.length === 3 || digits.length === 4) {
      const pair = (i: number) => parseInt(digits[i] + digits[i], 16)
      return {
        r: pair(0),
        g: pair(1),
        b: pair(2),
        a: digits.length === 4 ? pair(3) / 255 : 1,
      }
    }
    if (digits.length === 6 || digits.length === 8) {
      const pair = (i: number) => parseInt(digits.slice(i, i + 2), 16)
      return {
        r: pair(0),
        g: pair(2),
        b: pair(4),
        a: digits.length === 8 ? pair(6) / 255 : 1,
      }
    }
    return null
  }

  const fn = /^(rgba?|hsla?)\(([^)]*)\)$/.exec(value)
  if (!fn) return null
  const parts = fn[2].split(/[\s,/]+/).filter(Boolean)
  if (parts.length < 3) return null
  const alpha = parts.length > 3 ? unit(parts[3]) : 1

  if (fn[1].startsWith('rgb')) {
    return { r: byte(parts[0]), g: byte(parts[1]), b: byte(parts[2]), a: alpha }
  }
  const hue = parseFloat(parts[0])
  if (!Number.isFinite(hue)) return null
  return { ...hslToRgb(hue, unit(parts[1]), unit(parts[2])), a: alpha }
}

function toHex({ r, g, b }: Rgba): string {
  return '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')
}

/**
 * What goes on the pill. The card already prints the value as the palette stores
 * it, so the pill carries the *other* notation — hex for an rgb()/hsl() swatch,
 * rgb() for a hex one — which is the conversion anyone reading a palette does by
 * hand, and it keeps the site's little gray chip from being decoration.
 */
function alternateNotation(raw: string, color: Rgba): string {
  const isHex = raw.trim().startsWith('#')
  const base = isHex ? `rgb(${color.r}, ${color.g}, ${color.b})` : toHex(color)
  if (color.a >= 1) return base
  return `${base} · ${Math.round(color.a * 100)}%`
}

/* ------------------------------------------------------------------ cards -- */

function renderCard(x: number, y: number, swatch: PaletteColor): string {
  const raw = String(swatch.color ?? '')
  const color = parseColor(raw)
  // A color we could not parse is handed to the renderer as-is: browsers accept
  // named colors and modern color functions that this parser does not.
  const fill = color ? toHex(color) : escapeXML(raw) || 'none'
  const opacity = color && color.a < 1 ? ` fill-opacity="${n(color.a)}"` : ''

  const parts: string[] = [`<g transform="translate(${n(x)} ${n(y)})">`]

  // Everything inside the frame, clipped to the inner radius — the component's
  // `overflow-hidden` + `rounded-lg`. Every shape whose geometry is the same on
  // every card is a <defs> reference, which is most of a card's markup: a
  // 150-swatch code palette would otherwise repeat it 150 times.
  parts.push('<g clip-path="url(#cz-card)">')
  parts.push(use('cz-panel'))
  // Transparency reads as a checkerboard, as it does on the card.
  if (color && color.a < 1) parts.push(use('cz-alpha'))
  parts.push(
    `<rect x="${CARD.border}" y="${CARD.border}" width="${INNER_W}" height="${n(COLOR_H)}" fill="${fill}"${opacity}/>`,
  )
  // The belly: white below the wave, color above it.
  parts.push(use('cz-belly'))
  parts.push('</g>')
  parts.push(use('cz-frame'))

  const name = fitText(String(swatch.name ?? ''), TEXT_W, 12)
  const value = fitText(raw, TEXT_W, 12, true)
  parts.push(
    `<text x="${CARD.padX}" y="${n(NAME_BASELINE)}" font-size="12" fill="${THEME.muted}">${escapeXML(name)}</text>`,
  )
  parts.push(
    `<text x="${CARD.padX}" y="${n(VALUE_BASELINE)}" font-size="12" font-weight="700" fill="${THEME.value}">${escapeXML(value)}</text>`,
  )

  if (color) {
    // The pill is sized around its label (px-2, so 8px a side), and the label is
    // then *centred* in it rather than set from the left edge. Its width can only
    // ever be an estimate — more so when the reader has no Inter and the sheet
    // renders in a fallback face — and centring splits that error evenly instead
    // of letting all of it land on the right as visibly short padding.
    const label = fitText(alternateNotation(raw, color), TEXT_W - PILL_PAD_X * 2, PILL_SIZE, true)
    const pillW = Math.min(TEXT_W, Math.round(textWidth(label, PILL_SIZE, true)) + PILL_PAD_X * 2)
    parts.push(
      `<rect x="${CARD.padX}" y="${n(PILL_TOP)}" width="${pillW}" height="${PILL_H}" rx="${PILL_H / 2}" fill="${THEME.pill}"/>`,
    )
    parts.push(
      `<text x="${n(CARD.padX + pillW / 2)}" y="${n(PILL_BASELINE)}" text-anchor="middle" font-size="${PILL_SIZE}" font-weight="600" fill="${THEME.pillText}">${escapeXML(label)}</text>`,
    )
  }

  parts.push('</g>')
  return parts.join('\n')
}

/* ------------------------------------------------------------------ sheet -- */

export function buildPaletteSvg(groups: PaletteGroup[]): string {
  const filled = groups.filter((group) => Array.isArray(group.colors) && group.colors.length > 0)
  const total = filled.reduce((count, group) => count + group.colors.length, 0)
  const widest = filled.reduce((max, group) => Math.max(max, group.colors.length), 1)

  // A two-color palette should not open as a mostly empty poster, so the sheet is
  // only as wide as its biggest group needs, up to the site's own column count —
  // but never so narrow that the header or the footer runs off the edge.
  const columns = clamp(widest, 1, SHEET.maxColumns)
  const subtitle =
    total === 0
      ? 'Color palette'
      : `Color palette · ${plural(total, 'color')} · ${plural(filled.length, 'group')}`
  const footnote = 'Generated with colorize.design — color palettes from any website.'
  const empty = 'This palette has no colors yet.'
  const contentW = Math.ceil(
    Math.max(
      columns * CARD.width + (columns - 1) * SHEET.gap,
      MARK.gutter + textWidth('colorize.design', 18, true),
      MARK.gutter + textWidth(subtitle, 12),
      textWidth(footnote, 11.5),
      filled.length === 0 ? textWidth(empty, 14) : 0,
    ),
  )
  const width = contentW + SHEET.pad * 2

  const body: string[] = []

  // Header: brand mark, wordmark, and what the sheet contains.
  body.push(
    `<g transform="translate(${SHEET.pad} 37) scale(${n(30 / MARK.size)})">`,
    `<path fill-rule="evenodd" clip-rule="evenodd" d="${MARK.arc}" fill="${THEME.accent}"/>`,
    `<path fill-rule="evenodd" clip-rule="evenodd" d="${MARK.blob}" fill="url(#cz-mark)"/>`,
    '</g>',
    `<text x="${SHEET.pad + MARK.gutter}" y="48" font-size="18" font-weight="700" fill="${THEME.heading}">colorize.design</text>`,
    `<text x="${SHEET.pad + MARK.gutter}" y="66" font-size="12" fill="${THEME.muted}">${escapeXML(subtitle)}</text>`,
    `<rect x="${SHEET.pad}" y="88" width="${contentW}" height="1" fill="${THEME.hairline}"/>`,
  )

  let y = 88 + SHEET.groupGap

  if (filled.length === 0) {
    body.push(
      `<text x="${SHEET.pad}" y="${y + 12}" font-size="14" fill="${THEME.muted}">${empty}</text>`,
    )
    y += 24
  }

  for (const group of filled) {
    const count = plural(group.colors.length, 'color')
    const title = fitText(
      String(group.name ?? ''),
      contentW - textWidth(count, 12) - 12,
      SHEET.headingSize,
      true,
    )
    body.push(
      `<text x="${SHEET.pad}" y="${y + 12}" font-size="${SHEET.headingSize}" font-weight="700" fill="${THEME.heading}">${escapeXML(title)}</text>`,
      `<text x="${n(SHEET.pad + textWidth(title, SHEET.headingSize, true) + 10)}" y="${y + 12}" font-size="12" fill="${THEME.muted}">${count}</text>`,
    )
    y += SHEET.headingSize + SHEET.headingGap

    group.colors.forEach((swatch, index) => {
      const column = index % columns
      const row = Math.floor(index / columns)
      body.push(
        renderCard(
          SHEET.pad + column * (CARD.width + SHEET.gap),
          y + row * (CARD_H + SHEET.gap),
          swatch,
        ),
      )
    })

    const rows = Math.ceil(group.colors.length / columns)
    y += rows * CARD_H + (rows - 1) * SHEET.gap + SHEET.groupGap
  }

  const footerRule = y
  const footerBaseline = footerRule + 22
  const height = footerBaseline + 26
  body.push(
    `<rect x="${SHEET.pad}" y="${footerRule}" width="${contentW}" height="1" fill="${THEME.hairline}"/>`,
    `<text x="${SHEET.pad}" y="${footerBaseline}" font-size="11.5" fill="${THEME.muted}">Generated with colorize.design — color palettes from any website.</text>`,
  )

  const label = total === 0 ? 'Colorize palette' : `Colorize palette — ${plural(total, 'color')}`

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="${escapeXML(FONT_FAMILY)}">`,
    `<title>${escapeXML(label)}</title>`,
    '<desc>Generated with colorize.design</desc>',
    '<defs>',
    // The card, minus its color: the clip that stands in for `overflow-hidden`,
    // the white panel, the checkerboard behind a translucent swatch, the belly
    // divider at the scale this card width implies, and the 4px frame — a
    // centred stroke, so the outer radius lands on rounded-lg (8) and the inner
    // edge meets the clip.
    `<clipPath id="cz-card"><rect x="${CARD.border}" y="${CARD.border}" width="${INNER_W}" height="${INNER_H}" rx="${CARD.radius - CARD.border}"/></clipPath>`,
    `<rect id="cz-panel" x="${CARD.border}" y="${CARD.border}" width="${INNER_W}" height="${INNER_H}" fill="${THEME.panel}"/>`,
    `<rect id="cz-alpha" x="${CARD.border}" y="${CARD.border}" width="${INNER_W}" height="${n(COLOR_H)}" fill="url(#cz-checker)"/>`,
    `<g id="cz-belly" transform="translate(${CARD.border} ${CARD.border + CARD.colorBand}) scale(${n(BELLY_SCALE, 5)})"><path d="${BELLY.path}" fill="${THEME.panel}"/></g>`,
    `<rect id="cz-frame" x="${CARD.border / 2}" y="${CARD.border / 2}" width="${CARD.width - CARD.border}" height="${n(CARD_H - CARD.border)}" rx="${CARD.radius - CARD.border / 2}" fill="none" stroke="${THEME.cardEdge}" stroke-width="${CARD.border}"/>`,
    `<pattern id="cz-checker" width="16" height="16" patternUnits="userSpaceOnUse">`,
    `<rect width="16" height="16" fill="${THEME.panel}"/>`,
    `<path d="M0 0h8v8H0zM8 8h8v8H8z" fill="${THEME.checker}"/>`,
    '</pattern>',
    '<linearGradient id="cz-mark" x1="19.5536" y1="16.7222" x2="7.42018" y2="13.7416" gradientUnits="userSpaceOnUse">',
    '<stop stop-color="#8D8FF8"/>',
    '<stop offset="1" stop-color="#D6D6FF"/>',
    '</linearGradient>',
    '</defs>',
    `<rect width="${width}" height="${height}" fill="${THEME.canvas}"/>`,
    ...body,
    '</svg>',
    '',
  ].join('\n')
}
